import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, patch, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { runCompetitionOperation } from "pages/EventsManagement/components/Tabs/Competitions/competitionOperation"
import { proposalMessage } from "./proposalMessages"

// Runs one proposals command at a time (ADR 0015): the gateway answers 202, Events confirms the result on events-response, and
// the operation read answers the polling fallback, as Logistics does (useLogisticsOperation). Every send is a first send, so a
// 4xx from the gateway never reached Events and ends refused at once. `messageForCode` words a refusal for where it shows.
export default function useProposalOperation(messageForCode = proposalMessage) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const active = useRef(null)
  const mounted = useRef(true)
  const [pending, setPending] = useState(null)
  // Leaving stops observing the command, not the command: one sent as the page closes (an Undo's time cut short) still reaches Events.
  useEffect(() => () => { mounted.current = false; active.current?.cancel(); active.current = null }, [])
  // `action` is the module's name of the command (create, edit, delete, withdraw, reopen), which its result carries.
  const run = useCallback((action, path, body, method = "post") => {
    if (active.current) return Promise.resolve({ status: "busy" })
    const operation = { action, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId() }
    const send = method === "patch" ? patch : post
    const runner = runCompetitionOperation({ operation, socket, operationKind: "tournament-proposal-operation", messageForCode,
      scopeKeys: [], firstSubmission: true, read: operationId => get(`/admin/tournament-proposal-operations/${operationId}`),
      submit: () => send(path, { ...body, operationId: operation.operationId, socketClientId }, { headers: { "X-Correlation-Id": operation.correlationId } }) })
    if (!mounted.current) return runner.promise
    active.current = runner
    setPending(action)
    return runner.promise.then(result => {
      if (active.current === runner) {
        active.current = null
        if (mounted.current) setPending(null)
      }
      return result
    })
  }, [messageForCode, socket, socketClientId])
  return { pending, run }
}
